/**
 * Request detail — and the approval screen.
 *
 * This is the most important screen in the system. An approver arriving from an
 * email on a phone must be able to see, without scrolling: the amount, who asked,
 * why, what the budget looks like afterwards, and the quotation. Then decide.
 *
 * The actions shown come from the server (`availableActions`), never from the UI's
 * own reasoning. That way a hidden button and a refused request can never disagree.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { REQUEST_STATUS_META, formatMoney } from '@asp/shared';
import type { RequestStatus } from '@asp/shared';
import { api, ApiError, openAuthenticated } from '../lib/api.ts';
import { formatDate, formatDateTime, formatRelative, humanise } from '../lib/format.ts';
import {
  Badge,
  Banner,
  Button,
  Card,
  DetailItem,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '../components/ui.tsx';

interface Detail {
  request: Record<string, any>;
  lines: Array<Record<string, any>>;
  approvalSteps: Array<Record<string, any>>;
  attachments: Array<Record<string, any>>;
  comments: Array<Record<string, any>>;
  availableActions: string[];
  budget: {
    passed: boolean;
    blocked: boolean;
    requires_override_reason: boolean;
    enforcement_mode: string;
    warnings: Array<{
      level: string;
      label: string;
      annual_amount: string;
      available_budget: string;
      request_amount: string;
      shortfall: string;
      severity: string;
    }>;
  } | null;
  purchaseOrder: Record<string, any> | null;
}

const ACTION_LABELS: Record<string, string> = {
  submit: 'Submit for approval',
  resubmit: 'Resubmit',
  approve: 'Approve',
  recommend: 'Recommend',
  reject: 'Reject',
  return: 'Return for changes',
  cancel: 'Cancel request',
};

export function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['request', id],
    queryFn: () => api.get<Detail>(`/api/requests/${id}`),
    enabled: Boolean(id),
  });

  const transition = useMutation({
    mutationFn: (payload: { action: string; comment?: string; budgetOverrideReason?: string }) =>
      api.post<{ status: string; message: string }>(`/api/requests/${id}/transition`, payload),
    onSuccess: (result) => {
      setPendingAction(null);
      setComment('');
      setOverrideReason('');
      setActionError(null);
      setFlash(result.message);
      void queryClient.invalidateQueries({ queryKey: ['request', id] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
    onError: (err) => {
      setActionError(err instanceof ApiError ? err.message : 'That could not be completed.');
    },
  });

  if (isPending) return <Card><LoadingState label="Loading request" /></Card>;
  if (error) {
    return (
      <Card>
        <ErrorState error={error} onRetry={refetch} />
        <div className="mt-2 text-center">
          <Link to="/requests">
            <Button variant="ghost">Back to requests</Button>
          </Link>
        </div>
      </Card>
    );
  }

  const { request, lines, approvalSteps, attachments, comments, availableActions, budget, purchaseOrder } = data!;
  const meta = REQUEST_STATUS_META[request.status as RequestStatus];

  const shortfall = budget?.warnings.find((w) => w.severity === 'insufficient');
  const needsOverride = Boolean(budget?.requires_override_reason) && !request.budget_override_reason;
  const commentRequired = pendingAction === 'reject' || pendingAction === 'return';

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/requests" className="hover:underline">
            ← Purchase requests
          </Link>
        }
        title={request.request_number ?? 'Draft request'}
        description={`${request.school_name} · ${request.department_name} · raised by ${request.requester_name}`}
        actions={<Badge tone={meta?.tone ?? 'neutral'}>{meta?.label ?? humanise(request.status)}</Badge>}
      />

      {flash && (
        <div className="mb-4">
          <Banner tone="success">{flash}</Banner>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/* ------------------------------------------------------------ */}
        {/* Main column */}
        {/* ------------------------------------------------------------ */}
        <div className="space-y-4">
          {/* The decision summary. Deliberately first and compact: on a phone this
              is the whole screen, and it contains everything needed to decide. */}
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[12px] font-medium uppercase tracking-wide text-[var(--ink-muted)]">
                  Total including VAT
                </p>
                <p className="mt-0.5 text-3xl font-semibold tabular leading-none text-[var(--ink-primary)]">
                  {formatMoney(request.estimated_total)}
                </p>
                <p className="mt-1 text-[12px] text-[var(--ink-muted)]">
                  {formatMoney(request.subtotal)} net + {formatMoney(request.vat_total)} VAT
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {request.requires_director_approval && <Badge tone="info">Needs senior approval</Badge>}
                {request.priority === 'urgent' && <Badge tone="danger">Urgent</Badge>}
                {request.duplicate_of_number && (
                  <Badge tone="warning">Similar to {request.duplicate_of_number}</Badge>
                )}
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailItem label="Supplier">
                {request.supplier_name ?? '—'}
                {request.is_new_supplier && (
                  <span className="ml-1.5">
                    <Badge tone="warning" dot={false}>
                      New — needs vetting
                    </Badge>
                  </span>
                )}
              </DetailItem>
              <DetailItem label="Needed by">{formatDate(request.required_delivery_date)}</DetailItem>
              <DetailItem label="Priority">{humanise(request.priority)}</DetailItem>
              <DetailItem label="Budget code">{request.budget_code ?? '—'}</DetailItem>
              <DetailItem label="Cost centre">{request.cost_centre ?? '—'}</DetailItem>
              <DetailItem label="Submitted">
                {request.submitted_at ? formatRelative(request.submitted_at) : 'Not yet'}
              </DetailItem>
            </dl>

            <div className="mt-5 border-t border-[var(--border-subtle)] pt-4">
              <DetailItem label="Why this is needed">
                <p className="whitespace-pre-wrap text-[13px] leading-relaxed">
                  {request.business_justification || <span className="text-[var(--ink-muted)]">Not given</span>}
                </p>
              </DetailItem>
            </div>
          </Card>

          {/* Budget consequence, stated before the decision rather than after it. */}
          {budget && shortfall && (
            <Banner
              tone={budget.blocked ? 'danger' : 'warning'}
              title={
                budget.blocked
                  ? 'This exceeds the budget and this school does not permit overspend'
                  : `This exceeds the ${shortfall.label} budget by ${formatMoney(shortfall.shortfall)}`
              }
            >
              <p>
                {shortfall.label} has {formatMoney(shortfall.available_budget)} available of{' '}
                {formatMoney(shortfall.annual_amount)}. This request is {formatMoney(shortfall.request_amount)}.
              </p>
              {!budget.blocked && (
                <p className="mt-1">
                  It can still be approved with a written justification, and will then also need
                  senior approval regardless of its value.
                </p>
              )}
            </Banner>
          )}

          {request.budget_override_reason && (
            <Banner tone="info" title="Approved over budget">
              <p className="whitespace-pre-wrap">{request.budget_override_reason}</p>
              <p className="mt-1 text-[12px]">
                Authorised by {request.budget_override_by_name} on{' '}
                {formatDateTime(request.budget_override_at)}
              </p>
            </Banner>
          )}

          {/* Items */}
          <Card title="Items" padded={false}>
            <Table>
              <thead>
                <tr>
                  <Th>Description</Th>
                  <Th numeric>Qty</Th>
                  <Th numeric>Unit price</Th>
                  <Th numeric>VAT</Th>
                  <Th numeric>Total</Th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <Tr key={line.id}>
                    <Td>{line.description}</Td>
                    <Td numeric>
                      {Number(line.quantity)} {line.uom ?? ''}
                    </Td>
                    <Td numeric>{formatMoney(line.unit_price)}</Td>
                    <Td numeric>{Number(line.vat_rate)}%</Td>
                    <Td numeric className="font-semibold">
                      {formatMoney(line.line_total)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <Td className="font-semibold" />
                  <Td />
                  <Td />
                  <Td numeric className="font-semibold">
                    Total
                  </Td>
                  <Td numeric className="font-semibold">
                    {formatMoney(request.estimated_total)}
                  </Td>
                </tr>
              </tfoot>
            </Table>
          </Card>

          {/* Attachments. A quotation an approver cannot open is no evidence at all. */}
          <Card title="Quotations and documents" padded={false}>
            {attachments.length === 0 ? (
              <p className="px-4 py-6 text-center text-[13px] text-[var(--ink-muted)]">
                Nothing attached.
              </p>
            ) : (
              <ul className="divide-y divide-[var(--border-subtle)]">
                {attachments.map((file) => (
                  <li key={file.id} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="grid size-8 shrink-0 place-items-center rounded bg-[var(--surface-3)] text-[var(--ink-secondary)]">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="size-4" aria-hidden>
                        <path d="M5 2h7l4 4v12H5zm7 1.5V6h2.5z" />
                      </svg>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium text-[var(--ink-primary)]">
                        {file.filename}
                      </p>
                      <p className="text-[12px] text-[var(--ink-muted)]">
                        {humanise(file.kind)} · {Math.round(Number(file.size_bytes) / 1024)} KB ·{' '}
                        {file.uploaded_by_name}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void openAuthenticated(`/api/attachments/${file.id}`, window.alert)}
                    >
                      Open
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {comments.length > 0 && (
            <Card title="Comments" padded={false}>
              <ul className="divide-y divide-[var(--border-subtle)]">
                {comments.map((c) => (
                  <li key={c.id} className="px-4 py-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-[13px] font-medium text-[var(--ink-primary)]">{c.author_name}</p>
                      <p className="text-[12px] text-[var(--ink-muted)]">{formatRelative(c.created_at)}</p>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-[13px] text-[var(--ink-secondary)]">
                      {c.body}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* ------------------------------------------------------------ */}
        {/* Side column */}
        {/* ------------------------------------------------------------ */}
        <div className="space-y-4">
          {/* Actions. Sticky on desktop so the decision is always reachable; on
              mobile it sits above the approval trail, which is reference material. */}
          {availableActions.length > 0 && (
            <Card title="Your decision" className="lg:sticky lg:top-16">
              {actionError && (
                <div className="mb-3">
                  <Banner tone="danger">{actionError}</Banner>
                </div>
              )}

              {pendingAction ? (
                <div className="space-y-3">
                  <p className="text-[13px] font-medium text-[var(--ink-primary)]">
                    {ACTION_LABELS[pendingAction] ?? humanise(pendingAction)}
                  </p>

                  <Field
                    label={commentRequired ? 'Reason' : 'Comment'}
                    required={commentRequired}
                    hint={
                      commentRequired
                        ? 'The requester will see this, and it is recorded permanently.'
                        : 'Optional.'
                    }
                  >
                    <Textarea
                      autoFocus
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={
                        pendingAction === 'return'
                          ? 'What needs changing?'
                          : pendingAction === 'reject'
                            ? 'Why is this not approved?'
                            : ''
                      }
                    />
                  </Field>

                  {pendingAction === 'approve' && needsOverride && (
                    <Field
                      label="Justification for exceeding the budget"
                      required
                      hint="Required because this request exceeds its budget. Recorded against your name."
                    >
                      <Textarea
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="Where the money is coming from, and who agreed it."
                      />
                    </Field>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant={pendingAction === 'reject' ? 'danger' : pendingAction === 'approve' ? 'approve' : 'primary'}
                      size="lg"
                      loading={transition.isPending}
                      disabled={
                        (commentRequired && comment.trim().length === 0) ||
                        (pendingAction === 'approve' && needsOverride && overrideReason.trim().length === 0)
                      }
                      onClick={() =>
                        transition.mutate({
                          action: pendingAction,
                          comment: comment.trim() || undefined,
                          budgetOverrideReason: overrideReason.trim() || undefined,
                        })
                      }
                    >
                      Confirm
                    </Button>
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() => {
                        setPendingAction(null);
                        setActionError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableActions.includes('approve') && (
                    <Button variant="approve" size="lg" full onClick={() => setPendingAction('approve')}>
                      Approve
                    </Button>
                  )}
                  {availableActions.includes('recommend') && (
                    <Button variant="approve" size="lg" full onClick={() => setPendingAction('recommend')}>
                      Recommend
                    </Button>
                  )}
                  {availableActions.includes('return') && (
                    <Button size="lg" full onClick={() => setPendingAction('return')}>
                      Return for changes
                    </Button>
                  )}
                  {availableActions.includes('reject') && (
                    <Button variant="danger" size="lg" full onClick={() => setPendingAction('reject')}>
                      Reject
                    </Button>
                  )}
                  {(availableActions.includes('submit') || availableActions.includes('resubmit')) && (
                    <>
                      <Button
                        variant="primary"
                        size="lg"
                        full
                        loading={transition.isPending}
                        onClick={() =>
                          transition.mutate({
                            action: availableActions.includes('submit') ? 'submit' : 'resubmit',
                          })
                        }
                      >
                        {availableActions.includes('submit') ? 'Submit for approval' : 'Resubmit'}
                      </Button>
                      <Button
                        variant="secondary"
                        full
                        onClick={() => navigate(`/requests/${id}/edit`)}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                  {availableActions.includes('cancel') && (
                    <Button variant="ghost" full onClick={() => setPendingAction('cancel')}>
                      Cancel request
                    </Button>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Approval trail. Shows the whole route including steps not yet reached,
              so everyone can see who still has to act. */}
          <Card title="Approval route" padded={false}>
            <ol className="divide-y divide-[var(--border-subtle)]">
              {approvalSteps.map((step) => {
                const tone =
                  step.status === 'approved'
                    ? 'success'
                    : step.status === 'rejected'
                      ? 'danger'
                      : step.status === 'returned'
                        ? 'warning'
                        : step.status === 'skipped'
                          ? 'neutral'
                          : 'pending';
                return (
                  <li key={step.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[13px] font-medium text-[var(--ink-primary)]">{step.label}</p>
                      <Badge tone={tone as 'success'}>{humanise(step.status)}</Badge>
                    </div>
                    {step.satisfied_by_name && (
                      <p className="mt-0.5 text-[12px] text-[var(--ink-muted)]">
                        {step.satisfied_by_name}
                        {step.on_behalf_of_name && ` on behalf of ${step.on_behalf_of_name}`}
                        {step.acted_at && ` · ${formatRelative(step.acted_at)}`}
                      </p>
                    )}
                    {step.approvals_required > 1 && step.status === 'pending' && (
                      <p className="mt-0.5 text-[12px] text-[var(--ink-muted)]">
                        {(step.actions ?? []).length} of {step.approvals_required} approvals received
                      </p>
                    )}
                    {step.comment && (
                      <p className="mt-1 whitespace-pre-wrap rounded bg-[var(--surface-2)] px-2 py-1.5 text-[12px] text-[var(--ink-secondary)]">
                        {step.comment}
                      </p>
                    )}
                  </li>
                );
              })}
              {approvalSteps.length === 0 && (
                <li className="px-4 py-6 text-center text-[13px] text-[var(--ink-muted)]">
                  The approval route is worked out when the request is submitted.
                </li>
              )}
            </ol>
          </Card>

          {purchaseOrder && (
            <Card title="Purchase order">
              <dl className="space-y-3">
                <DetailItem label="Order number">{purchaseOrder.po_number}</DetailItem>
                <DetailItem label="Status">
                  <Badge tone="info">{humanise(purchaseOrder.status)}</Badge>
                </DetailItem>
                <DetailItem label="Sent to supplier">
                  {purchaseOrder.sent_at ? formatDateTime(purchaseOrder.sent_at) : 'Not yet'}
                </DetailItem>
                <DetailItem label="Expected delivery">
                  {formatDate(purchaseOrder.expected_delivery_date)}
                </DetailItem>
              </dl>
            </Card>
          )}

          <Card title="History" padded={false}>
            <AuditTrail entityId={id!} />
          </Card>
        </div>
      </div>
    </>
  );
}

function AuditTrail({ entityId }: { entityId: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['audit', 'purchase_requests', entityId],
    queryFn: () =>
      api.get<{ entries: Array<Record<string, any>> }>(`/api/audit/purchase_requests/${entityId}`),
  });

  if (isPending) return <LoadingState label="Loading history" />;
  if (error) return <p className="px-4 py-4 text-[13px] text-[var(--ink-muted)]">History unavailable.</p>;

  const entries = data!.entries.filter((e) => e.new_status || e.action !== 'updated');

  return (
    <ol className="divide-y divide-[var(--border-subtle)]">
      {entries.map((entry) => (
        <li key={entry.id} className="px-4 py-2.5">
          <p className="text-[12px] text-[var(--ink-primary)]">
            <span className="font-medium">
              {entry.actor_name ?? (entry.is_system_action ? 'System' : 'Unknown')}
            </span>{' '}
            {entry.new_status
              ? `moved this to ${humanise(entry.new_status).toLowerCase()}`
              : humanise(entry.action).toLowerCase()}
          </p>
          <p className="text-[11px] text-[var(--ink-muted)]">{formatDateTime(entry.occurred_at)}</p>
          {entry.comment && (
            <p className="mt-1 text-[12px] italic text-[var(--ink-secondary)]">“{entry.comment}”</p>
          )}
        </li>
      ))}
      {entries.length === 0 && (
        <li className="px-4 py-4 text-center text-[13px] text-[var(--ink-muted)]">Nothing recorded yet.</li>
      )}
    </ol>
  );
}
